import { createQuery } from '@amzn/gsce-shared-react-lib';

import { auditInsightsService } from 'src/data/api/starlight-service/auditInsightsService';
import {
  INSIGHT_STATUS,
  type GetAuditInsightsRequest,
  type GetAuditInsightsResponse,
} from 'src/types/starlight/AuditInsights';
import { withRetry } from 'src/v2/shared/data';
import { AuditInsightsQueryKeys } from './keys';

/**
 * How often to re-read while generation is still running. Generation is asynchronous —
 * completing an audit enqueues it — so a first view usually lands on PENDING.
 */
const PENDING_POLL_INTERVAL_MS = 10000;

/**
 * How many reads to spend on one PENDING audit before giving up.
 *
 * PENDING has no deadline of its own: an audit whose generation event was lost reports it
 * forever, so an unbounded interval would re-read a dead audit for as long as the tab stays
 * open. Sixty reads is more than a generation that succeeds needs — including one
 * redelivered after a guardrail block — and few enough that a lost event stops costing
 * requests.
 *
 * The budget is in reads, not in elapsed time. The timed poll spends one every ten seconds,
 * but `refetchOnWindowFocus` is left on, so returning to the tab spends one too: a reader
 * who switches away and back repeatedly runs out sooner than the poll alone would. Focus
 * refetch is kept because the interval pauses while the tab is hidden, so that read is what
 * shows a returning reader the current state rather than up to ten more seconds of a stale
 * banner.
 *
 * Giving up is not the same as concluding failure. The read is still PENDING and may yet
 * settle; the page has simply stopped asking.
 *
 * The count is the cached query entry's, not the mounted tab's, so it is a budget per audit
 * rather than per visit: leaving the page and returning resumes where it left off while the
 * entry is still cached, and only a reload or React Query's garbage collection starts it
 * over. That is the intent — a budget reset by remounting would be no budget at all, since
 * the tab unmounts whenever the reader switches to Summary.
 */
const MAX_PENDING_READS = 60;

/**
 * How long to wait before re-reading, given the last response and the reads spent so far.
 *
 * READY, FAILED and NOT_APPLICABLE are settled, so there is nothing to wait for and polling
 * stops. PENDING keeps polling up to `MAX_PENDING_READS`.
 *
 * @param data - The last response, absent before the first read resolves.
 * @param reads - Reads attempted against this audit, successful or not.
 */
export function pollWhilePending(
  data: GetAuditInsightsResponse | undefined,
  reads: number,
): number | false {
  if (data?.status !== INSIGHT_STATUS.PENDING) return false;
  return reads < MAX_PENDING_READS ? PENDING_POLL_INTERVAL_MS : false;
}

/**
 * Adapts React Query's query object to the rule above, which takes only plain values.
 *
 * Separate from `pollWhilePending` so the rule stays free of React Query's shape and both
 * halves can be exercised without a live query.
 *
 * Failed fetches are counted alongside successful ones. `dataUpdateCount` alone stops
 * advancing once reads start failing — the last PENDING response is retained — which would
 * hold the count below the cap and poll forever on exactly the outage the cap exists for.
 *
 * `dataUpdateCount` counts every successful read, the timed poll and a focus refetch alike,
 * so what this bounds is reads rather than elapsed time. See `MAX_PENDING_READS`.
 */
export function refetchIntervalFor(query: {
  state: {
    data: GetAuditInsightsResponse | undefined;
    dataUpdateCount: number;
    fetchFailureCount: number;
  };
}): number | false {
  const { data, dataUpdateCount, fetchFailureCount } = query.state;
  return pollWhilePending(data, dataUpdateCount + fetchFailureCount);
}

/**
 * Reads one audit's insight, polling while generation is still running.
 *
 * `withRetry` covers dropped connections and timeouts, matching `useQuestionQuery` and
 * `useListMediaByIdsQuery`. It does not retry 4xx or 5xx responses. A retry is worth more
 * here than on a one-shot read: every failed read counts against `MAX_PENDING_READS`, so a
 * blip during a PENDING wait spends part of the budget the wait needs.
 */
export const useAuditInsightsQuery = createQuery<
  GetAuditInsightsRequest,
  GetAuditInsightsResponse
>(
  AuditInsightsQueryKeys.insights,
  (input: GetAuditInsightsRequest) => withRetry(() => auditInsightsService.get(input)),
  {
    refetchInterval: refetchIntervalFor,
    // Already the tanstack-react-query default. Stated anyway, as the other polling queries
    // here do, so the interval above is read alongside its bound: a backgrounded tab waiting
    // on an audit that may never settle should not keep asking.
    refetchIntervalInBackground: false,
  },
);
